import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { SignOut, House, FileText, Upload, Coins, Package, Truck, CheckCircle, XCircle, Clock, Copy, Printer, Trash, Shield, Warning, ArrowCounterClockwise, Lightning } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import AdminNav from './AdminNav';
import { extractTxHash, isValidTxHash } from '../../lib/txHash';
import { sendUsdcRefund } from '../../lib/refundViaWallet';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending:              { label: 'Pending',       icon: Clock,       color: '#F59E0B' },
  pending_verification: { label: 'Verifying',     icon: Clock,       color: '#F59E0B' },
  paid:                 { label: 'Paid',          icon: Coins,       color: '#007AFF' },
  shipped:              { label: 'Shipped',       icon: Truck,       color: '#8B5CF6' },
  completed:            { label: 'Completed',     icon: CheckCircle, color: '#10B981' },
  canceled:             { label: 'Canceled',      icon: XCircle,     color: '#EF4444' },
};

const ESCROW_META = {
  held:     { label: 'In Escrow', color: '#007AFF' },
  released: { label: 'Released',  color: '#10B981' },
  refunded: { label: 'Refunded',  color: '#94A3B8' },
  disputed: { label: 'Disputed',  color: '#EF4444' },
};

const AdminWeb3Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, paid: 0, shipped: 0, completed: 0, canceled: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [tracking, setTracking] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundTxHash, setRefundTxHash] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  const [resolving, setResolving] = useState(false);
  const [trackingMissing, setTrackingMissing] = useState(false);
  const trackingInputRef = React.useRef(null);
  const [sendingRefund, setSendingRefund] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    loadOrders();
    loadStats();
  }, [statusFilter]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
      const r = await axios.get(`${API_URL}/api/admin/web3-orders${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(r.data.orders || []);
      setCounts(r.data.counts || {});
      setSelectedIds(new Set()); // reset selection on reload
    } catch (e) {
      toast.error('Failed to load orders');
      if (e.response?.status === 401) navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === orders.length && orders.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(orders.map(o => o.id)));
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const r = await axios.post(`${API_URL}/api/admin/web3-orders/bulk-delete`,
        { ids: Array.from(selectedIds) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Deleted ${r.data.deleted} order${r.data.deleted === 1 ? '' : 's'}`);
      setConfirmDelete(false);
      setSelectedIds(new Set());
      loadOrders();
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const issueRefund = async () => {
    if (!selectedOrder) return;
    const hash = extractTxHash(refundTxHash);
    if (!hash) {
      toast.error('Could not find a valid 0x + 64-hex tx hash. Paste the hash or a BaseScan/Etherscan URL.');
      return;
    }
    setResolving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const isDisputed = selectedOrder.escrow_state === 'disputed';
      const endpoint = isDisputed
        ? `/api/admin/web3-orders/${selectedOrder.id}/resolve-dispute`
        : `/api/admin/web3-orders/${selectedOrder.id}/refund`;
      const body = isDisputed
        ? { resolution: 'refund', refund_tx_hash: hash, notes: refundNotes }
        : { refund_tx_hash: hash, chain: selectedOrder.chain, notes: refundNotes };
      await axios.post(`${API_URL}${endpoint}`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Refund recorded — status flipped to Refunded');
      setRefundDialogOpen(false);
      setRefundTxHash('');
      setRefundNotes('');
      setSelectedOrder(null);
      loadOrders();
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally {
      setResolving(false);
    }
  };

  const resolveDisputeRelease = async () => {
    if (!selectedOrder) return;
    setResolving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API_URL}/api/admin/web3-orders/${selectedOrder.id}/resolve-dispute`,
        { resolution: 'release', notes: refundNotes || 'Admin confirmed shipment; releasing escrow.' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Dispute resolved — escrow released');
      setRefundNotes('');
      setSelectedOrder(null);
      loadOrders();
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resolve failed');
    } finally {
      setResolving(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const r = await axios.get(`${API_URL}/api/admin/web3-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(r.data);
    } catch (e) { /* silent */ }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const updateStatus = async (orderId, newStatus, trackingNumber = null, notes = null) => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({ status: newStatus });
      if (trackingNumber) params.append('tracking_number', trackingNumber);
      if (notes) params.append('notes', notes);
      await axios.put(`${API_URL}/api/admin/web3-orders/${orderId}/status?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Order → ${newStatus}`);
      setSelectedOrder(null);
      setTracking('');
      setNoteInput('');
      loadOrders();
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const explorerTx = (chain, hash) => {
    if (!hash) return '#';
    return chain === 'base'
      ? `https://basescan.org/tx/${hash}`
      : `https://etherscan.io/tx/${hash}`;
  };

  const copyText = (t) => {
    navigator.clipboard?.writeText(t);
    toast.info('Copied');
  };

  const printPackSlip = async (orderId) => {
    try {
      toast.loading('Generating pack slip…', { id: 'pack-slip-gen' });
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/admin/web3-orders/${orderId}/pack-slip`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to generate pack slip');
      const blob = await response.blob();
      const file = new File([blob], `PackSlip-${orderId}.pdf`, { type: 'application/pdf' });
      toast.dismiss('pack-slip-gen');

      // iOS / mobile: open native share sheet so user can text, email, print, or save
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            title: `Pack Slip ${orderId}`,
            text: `Warehouse pack slip for order ${orderId}`,
            files: [file]
          });
        } catch (shareErr) {
          // User canceled share — that's fine, do nothing
          if (shareErr?.name !== 'AbortError') throw shareErr;
        }
      } else {
        // Desktop fallback: open the PDF in a new tab
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        // Also offer a direct download
        const a = document.createElement('a');
        a.href = url;
        a.download = `PackSlip-${orderId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
    } catch (e) {
      toast.dismiss('pack-slip-gen');
      toast.error('Failed to generate pack slip');
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A]">
      <AdminNav title="Web3 Orders" titleTestId="admin-web3-title" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-4" data-testid="stats-total-orders">
              <p className="text-xs text-[#94A3B8] uppercase font-['JetBrains_Mono']">Total Orders</p>
              <p className="text-3xl font-['JetBrains_Mono'] font-bold text-[#007AFF]">{stats.total_orders}</p>
            </div>
            <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-4" data-testid="stats-revenue">
              <p className="text-xs text-[#94A3B8] uppercase font-['JetBrains_Mono']">Revenue (USD)</p>
              <p className="text-3xl font-['JetBrains_Mono'] font-bold text-[#10B981]">${stats.total_revenue_usd?.toFixed(0) || 0}</p>
            </div>
            <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-4">
              <p className="text-xs text-[#94A3B8] uppercase font-['JetBrains_Mono']">Units Sold</p>
              <p className="text-3xl font-['JetBrains_Mono'] font-bold text-[#F59E0B]">{stats.total_qty_sold || 0}</p>
            </div>
            <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-4">
              <p className="text-xs text-[#94A3B8] uppercase font-['JetBrains_Mono']">Unique Wallets</p>
              <p className="text-3xl font-['JetBrains_Mono'] font-bold text-cyan-400">{stats.unique_wallets || 0}</p>
            </div>
          </div>
        )}

        {/* Status tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <StatusTab active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} label="All" count={Object.values(counts).reduce((a, b) => a + b, 0)} color="#94A3B8" testid="tab-all" />
          {Object.entries(STATUS_META).map(([key, meta]) => (
            <StatusTab
              key={key}
              active={statusFilter === key}
              onClick={() => setStatusFilter(key)}
              label={meta.label}
              count={counts[key] || 0}
              color={meta.color}
              testid={`tab-${key}`}
            />
          ))}
        </div>

        {/* Bulk-delete toolbar */}
        <div className="flex items-center justify-between gap-3 mb-3 bg-[#11141D] border border-[#1E293B] rounded-lg px-4 py-3" data-testid="bulk-toolbar">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-[#94A3B8] font-['JetBrains_Mono']">
            <input
              type="checkbox"
              checked={orders.length > 0 && selectedIds.size === orders.length}
              onChange={toggleSelectAll}
              disabled={orders.length === 0}
              className="accent-[#007AFF] w-4 h-4 cursor-pointer"
              data-testid="select-all-orders"
            />
            <span>
              {selectedIds.size > 0
                ? <span className="text-[#F59E0B]">{selectedIds.size} selected</span>
                : <span>Select all ({orders.length})</span>}
            </span>
          </label>
          <Button
            onClick={() => setConfirmDelete(true)}
            disabled={selectedIds.size === 0}
            className="bg-[#EF4444] hover:bg-[#DC2626] text-white disabled:opacity-30 disabled:cursor-not-allowed"
            size="sm"
            data-testid="bulk-delete-orders-btn"
          >
            <Trash size={16} className="mr-1" /> Delete{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </Button>
        </div>

        {/* Orders list */}
        <div className="space-y-3" data-testid="web3-orders-list">
          {loading ? (
            <p className="text-center text-[#94A3B8] py-8">Loading…</p>
          ) : orders.length === 0 ? (
            <div className="text-center py-16 bg-[#11141D] border border-[#1E293B] rounded-lg">
              <Package size={48} className="mx-auto text-[#94A3B8] mb-3" />
              <p className="text-[#94A3B8]">No orders in this status.</p>
            </div>
          ) : (
            orders.map(o => {
              const Meta = STATUS_META[o.status] || STATUS_META.pending;
              const Icon = Meta.icon;
              const isSelected = selectedIds.has(o.id);
              return (
                <div key={o.id}
                  onClick={() => setSelectedOrder(o)}
                  className={`bg-[#11141D] border rounded-lg p-4 cursor-pointer hover:border-[#007AFF] transition-colors ${isSelected ? 'border-[#F59E0B]' : 'border-[#1E293B]'}`}
                  data-testid={`order-row-${o.id}`}>
                  <div className="flex flex-wrap items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(o.id)}
                      className="mt-1 accent-[#007AFF] w-4 h-4 cursor-pointer flex-shrink-0"
                      data-testid={`select-order-${o.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-mono font-bold text-cyan-400">{o.id}</p>
                        <span className="px-2 py-0.5 rounded text-xs font-bold font-['JetBrains_Mono']" style={{ background: `${Meta.color}22`, color: Meta.color }}>
                          <Icon size={12} className="inline mr-1" />{Meta.label}
                        </span>
                        {o.escrow_state && ESCROW_META[o.escrow_state] && (
                          <span className="px-2 py-0.5 rounded text-xs font-bold font-['JetBrains_Mono'] inline-flex items-center gap-1"
                                style={{ background: `${ESCROW_META[o.escrow_state].color}22`, color: ESCROW_META[o.escrow_state].color }}
                                data-testid={`escrow-badge-${o.id}`}>
                            <Shield size={11} weight="fill" />
                            {ESCROW_META[o.escrow_state].label}
                          </span>
                        )}
                        <span className="text-xs text-[#94A3B8]">{o.chain?.toUpperCase()} · {o.payment_token || 'ETH'}</span>
                      </div>
                      <p className="text-xs text-[#94A3B8] mt-1 font-mono truncate">Wallet: {o.wallet_address}</p>
                      <p className="text-xs text-[#94A3B8]">{o.shipping_address?.full_name} · {o.shipping_address?.city}, {o.shipping_address?.state} {o.shipping_address?.country}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#10B981]">${o.total_usd?.toFixed(2)}</p>
                      <p className="text-xs text-[#94A3B8]">{o.total_qty} pcs · {(o.items || []).length} SKUs</p>
                      <p className="text-xs text-[#94A3B8]">{new Date(o.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Bulk-delete confirmation dialog */}
      <Dialog open={confirmDelete} onOpenChange={(o) => !deleting && setConfirmDelete(o)}>
        <DialogContent className="bg-[#11141D] border-[#EF4444] text-white max-w-md" data-testid="bulk-delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-['JetBrains_Mono'] text-[#EF4444] flex items-center gap-2">
              <Trash size={20} /> Delete {selectedIds.size} order{selectedIds.size === 1 ? '' : 's'}?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#94A3B8]">
            This permanently removes the selected order record{selectedIds.size === 1 ? '' : 's'} from the database.
            On-chain transactions are unaffected. Inventory already decremented for shipped orders will NOT be restored.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={deleting} className="border-[#334155]">Cancel</Button>
            <Button onClick={bulkDelete} disabled={deleting} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-bulk-delete-orders">
              {deleting ? 'Deleting…' : `Delete ${selectedIds.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order detail */}
      <Dialog open={!!selectedOrder} onOpenChange={(o) => !o && setSelectedOrder(null)}>
        <DialogContent className="max-w-3xl bg-[#11141D] border-[#1E293B] text-white max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
          {selectedOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="font-['JetBrains_Mono'] text-cyan-400 flex items-center justify-between gap-2 pr-6">
                  <span>Order {selectedOrder.id}</span>
                  <Button onClick={() => printPackSlip(selectedOrder.id)} size="sm" className="bg-[#0047AB]" data-testid="print-pack-slip">
                    <Printer size={14} className="mr-1" /> Print Pack Slip
                  </Button>
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <Section title="Payment">
                  <Row label="Status" value={STATUS_META[selectedOrder.status]?.label || selectedOrder.status} />
                  <Row label="Chain" value={selectedOrder.chain?.toUpperCase()} />
                  <Row label="Token" value={selectedOrder.payment_token || 'ETH'} />
                  <Row label="Total" value={`$${selectedOrder.total_usd?.toFixed(2)}`} />
                  {selectedOrder.eth_price_at_order && (
                    <Row label="ETH @ order" value={`$${selectedOrder.eth_price_at_order.toFixed(2)}`} />
                  )}
                  <div className="pt-2">
                    <p className="text-xs text-[#94A3B8] mb-1">Buyer Wallet</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-mono break-all text-cyan-400">{selectedOrder.wallet_address}</p>
                      <Copy size={14} className="cursor-pointer text-[#94A3B8]" onClick={() => copyText(selectedOrder.wallet_address)} />
                    </div>
                  </div>
                  {selectedOrder.payment_tx_hash && (
                    <div className="pt-2">
                      <p className="text-xs text-[#94A3B8] mb-1">Payment Tx</p>
                      <a href={explorerTx(selectedOrder.chain, selectedOrder.payment_tx_hash)} target="_blank" rel="noopener noreferrer"
                        className="text-xs font-mono text-[#10B981] underline break-all" data-testid="tx-explorer-link">
                        {selectedOrder.payment_tx_hash}
                      </a>
                    </div>
                  )}
                </Section>

                <Section title="Shipping">
                  <Row label="Method" value={selectedOrder.shipping_method === 'ground_international' ? 'Intl Ground' : 'USA Ground'} />
                  <Row label="Cost" value={`$${selectedOrder.shipping_cost?.toFixed(2)}`} />
                  <div className="pt-2 text-xs space-y-1">
                    <p className="text-white">{selectedOrder.shipping_address?.full_name}</p>
                    {selectedOrder.shipping_address?.company && <p className="text-[#94A3B8]">{selectedOrder.shipping_address.company}</p>}
                    <p className="text-[#94A3B8]">{selectedOrder.shipping_address?.street}</p>
                    <p className="text-[#94A3B8]">{selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state} {selectedOrder.shipping_address?.zip_code}</p>
                    <p className="text-[#94A3B8]">{selectedOrder.shipping_address?.country}</p>
                  </div>
                  <div className="pt-2 text-xs">
                    <p className="text-[#94A3B8]">Contact</p>
                    <p>{selectedOrder.contact?.email} · {selectedOrder.contact?.phone}</p>
                  </div>
                  {selectedOrder.tracking_number && (
                    <Row label="Tracking" value={selectedOrder.tracking_number} />
                  )}
                </Section>
              </div>

              <div className="mt-4">
                <h4 className="font-['JetBrains_Mono'] text-[#007AFF] text-sm mb-2">Items</h4>
                <div className="space-y-1 text-sm">
                  {(selectedOrder.items || []).map((it, i) => (
                    <div key={i} className="bg-[#05050A] rounded p-2 flex justify-between">
                      <div className="min-w-0">
                        <p className="font-mono text-cyan-400">{it.part_number}</p>
                        <p className="text-xs text-[#94A3B8] truncate">{it.description}</p>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <p>{it.quantity} × ${it.unit_price?.toFixed(2)}</p>
                        <p className="text-[#10B981] font-bold">${it.line_total?.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="mt-4 p-3 bg-[#05050A] rounded space-y-3">
                <h4 className="font-['JetBrains_Mono'] text-[#F59E0B] text-sm">Admin Actions</h4>

                {/* Tracking number (big, labeled, distinct) */}
                <div className="space-y-1.5">
                  <label className="text-xs text-[#F59E0B] font-['JetBrains_Mono'] flex items-center gap-1 uppercase tracking-wider">
                    <Truck size={14} /> Tracking number
                    <span className="text-[#EF4444] ml-0.5">*</span>
                    <span className="text-[#64748B] normal-case tracking-normal ml-2">required to ship</span>
                  </label>
                  <Input
                    ref={trackingInputRef}
                    placeholder="e.g. 1Z888456671234"
                    value={tracking}
                    onChange={(e) => { setTracking(e.target.value); if (e.target.value) setTrackingMissing(false); }}
                    className={`bg-[#11141D] h-11 text-base font-mono ${trackingMissing ? 'border-2 border-[#EF4444] animate-pulse' : 'border-[#F59E0B]/40 focus:border-[#F59E0B]'}`}
                    data-testid="tracking-input"
                    autoComplete="off"
                    inputMode="text"
                  />
                  {tracking && (
                    <p className="text-[11px] text-[#10B981] font-['JetBrains_Mono']">
                      ✓ Will release ${selectedOrder.total_usd?.toFixed(2)} escrow on Mark Shipped
                    </p>
                  )}
                </div>

                {/* Notes (visually distinct from tracking) */}
                <div className="space-y-1.5">
                  <label className="text-xs text-[#64748B] font-['JetBrains_Mono'] flex items-center gap-1 uppercase tracking-wider">
                    Internal notes <span className="normal-case tracking-normal">(optional)</span>
                  </label>
                  <Textarea placeholder="e.g. Shipped via UPS Ground from Austin warehouse"
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    className="bg-transparent border-[#334155]" rows={2} data-testid="notes-input" />
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                  {selectedOrder.status === 'pending' && (
                    <Button onClick={() => updateStatus(selectedOrder.id, 'paid', null, noteInput)} className="bg-[#007AFF]" data-testid="mark-paid">Mark Paid</Button>
                  )}
                  {(selectedOrder.status === 'pending' || selectedOrder.status === 'paid') && (
                    <Button
                      onClick={() => {
                        if (!tracking || !tracking.trim()) {
                          setTrackingMissing(true);
                          toast.error('Enter tracking number in the amber-bordered box above');
                          setTimeout(() => trackingInputRef.current?.focus(), 100);
                          setTimeout(() => setTrackingMissing(false), 3000);
                          return;
                        }
                        updateStatus(selectedOrder.id, 'shipped', tracking.trim(), noteInput);
                      }}
                      className="bg-[#8B5CF6]" data-testid="mark-shipped">
                      <Truck size={14} className="mr-1" /> Mark Shipped (release escrow + deduct stock)
                    </Button>
                  )}
                  {selectedOrder.status === 'shipped' && (
                    <Button onClick={() => updateStatus(selectedOrder.id, 'completed', null, noteInput)} className="bg-[#10B981]" data-testid="mark-completed">Mark Completed</Button>
                  )}
                  {selectedOrder.status !== 'canceled' && selectedOrder.status !== 'completed' && (
                    <Button onClick={() => updateStatus(selectedOrder.id, 'canceled', null, noteInput)} variant="outline" className="border-[#EF4444] text-[#EF4444]" data-testid="mark-canceled">Cancel</Button>
                  )}
                </div>
              </div>

              {/* Escrow / Dispute / Refund panel */}
              {selectedOrder.escrow_state && (
                <div className="mt-4 p-3 bg-[#05050A] rounded space-y-3 border"
                     style={{ borderColor: `${ESCROW_META[selectedOrder.escrow_state]?.color || '#334155'}66` }}
                     data-testid="escrow-panel">
                  <h4 className="font-['JetBrains_Mono'] text-sm flex items-center gap-2"
                      style={{ color: ESCROW_META[selectedOrder.escrow_state]?.color }}>
                    <Shield size={16} weight="fill" />
                    Escrow: {ESCROW_META[selectedOrder.escrow_state]?.label}
                  </h4>
                  {selectedOrder.escrow_state === 'held' && (
                    <p className="text-xs text-[#94A3B8]">
                      Payment verified on-chain. Funds are held in escrow until you enter a tracking
                      number and mark the order Shipped — at which point escrow auto-releases.
                    </p>
                  )}
                  {selectedOrder.escrow_state === 'released' && (
                    <p className="text-xs text-[#10B981]">
                      Escrow released on {selectedOrder.escrow_released_at ? new Date(selectedOrder.escrow_released_at).toLocaleString() : '—'}
                      {selectedOrder.escrow_release_reason && <span className="text-[#64748B]"> · {selectedOrder.escrow_release_reason}</span>}
                    </p>
                  )}
                  {selectedOrder.escrow_state === 'disputed' && (
                    <div className="space-y-2">
                      <p className="text-xs text-[#EF4444] flex items-start gap-1">
                        <Warning size={14} className="flex-shrink-0 mt-0.5" weight="fill" />
                        <span>
                          Buyer disputed this order on {selectedOrder.disputed_at ? new Date(selectedOrder.disputed_at).toLocaleString() : '—'}.
                          {selectedOrder.dispute_reason && (
                            <span className="block text-[#94A3B8] italic mt-1">"{selectedOrder.dispute_reason}"</span>
                          )}
                        </span>
                      </p>
                    </div>
                  )}
                  {selectedOrder.escrow_state === 'refunded' && (
                    <p className="text-xs text-[#94A3B8]">
                      Refunded on {selectedOrder.escrow_refunded_at ? new Date(selectedOrder.escrow_refunded_at).toLocaleString() : '—'}
                      {selectedOrder.refund_tx_hash && (
                        <a href={explorerTx(selectedOrder.refund_chain || selectedOrder.chain, selectedOrder.refund_tx_hash)}
                           target="_blank" rel="noopener noreferrer"
                           className="block text-[#10B981] underline mt-1 break-all">
                          Refund tx: {selectedOrder.refund_tx_hash}
                        </a>
                      )}
                    </p>
                  )}
                  {/* Actions available per escrow state */}
                  <div className="flex flex-wrap gap-2">
                    {selectedOrder.escrow_state === 'disputed' && (
                      <>
                        <Button onClick={resolveDisputeRelease} disabled={resolving}
                                className="bg-[#10B981]" data-testid="resolve-release">
                          <CheckCircle size={14} className="mr-1" /> Resolve: Release
                        </Button>
                        <Button onClick={() => setRefundDialogOpen(true)} disabled={resolving}
                                variant="outline" className="border-[#EF4444] text-[#EF4444]" data-testid="resolve-refund">
                          <ArrowCounterClockwise size={14} className="mr-1" /> Resolve: Refund
                        </Button>
                      </>
                    )}
                    {(selectedOrder.escrow_state === 'held') && (
                      <Button onClick={() => setRefundDialogOpen(true)}
                              variant="outline" className="border-[#EF4444] text-[#EF4444]" data-testid="issue-refund">
                        <ArrowCounterClockwise size={14} className="mr-1" /> Issue Refund
                      </Button>
                    )}
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedOrder(null)} className="border-[#334155]">Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Refund dialog */}
      <Dialog open={refundDialogOpen} onOpenChange={(o) => !resolving && setRefundDialogOpen(o)}>
        <DialogContent className="bg-[#11141D] border-[#EF4444] text-white max-w-md" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="font-['JetBrains_Mono'] text-[#EF4444] flex items-center gap-2">
              <ArrowCounterClockwise size={20} /> Issue Refund
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (() => {
            const hashValid = isValidTxHash(extractTxHash(refundTxHash));
            const copy = (label, text) => { navigator.clipboard?.writeText(text); toast.success(`${label} copied`); };
            return (
              <div className="space-y-4">
                {/* Amount + Chain banner */}
                <div className="bg-[#0B1120] border border-[#10B981]/40 rounded-lg p-4 text-center">
                  <p className="text-[10px] uppercase text-[#64748B] font-['JetBrains_Mono']">Send this exact amount</p>
                  <div className="flex items-center justify-center gap-2 mt-1">
                    <p className="text-3xl font-['JetBrains_Mono'] font-bold text-[#10B981]">
                      ${selectedOrder.total_usd?.toFixed(2)}
                    </p>
                    <span className="text-sm text-[#94A3B8]">{selectedOrder.payment_token || 'USDC'}</span>
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-1">on <span className="font-bold">{selectedOrder.chain?.toUpperCase()}</span> network</p>
                  <button onClick={() => copy('Amount', selectedOrder.total_usd?.toFixed(2))}
                          className="mt-2 text-[10px] text-[#007AFF] hover:text-white inline-flex items-center gap-1 font-['JetBrains_Mono']"
                          data-testid="copy-refund-amount">
                    <Copy size={10} /> copy amount
                  </button>
                </div>

                {/* Destination wallet — big & copyable */}
                <div className="bg-[#0B1120] border border-[#334155] rounded-lg p-4">
                  <p className="text-[10px] uppercase text-[#64748B] font-['JetBrains_Mono'] mb-1">Send back to buyer wallet</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-cyan-300 text-xs break-all font-mono">{selectedOrder.wallet_address}</code>
                    <button onClick={() => copy('Address', selectedOrder.wallet_address)}
                            className="flex-shrink-0 px-2 py-1 rounded bg-[#007AFF] hover:bg-[#0047AB] text-white text-xs font-['JetBrains_Mono'] inline-flex items-center gap-1"
                            data-testid="copy-refund-address">
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                </div>

                {/* One-tap refund from admin's connected wallet */}
                <div className="bg-[#0B1120] border border-[#F59E0B]/40 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Lightning size={16} className="text-[#F59E0B]" weight="fill" />
                    <span className="text-xs font-['JetBrains_Mono'] text-[#F59E0B]">One-tap refund</span>
                  </div>
                  <p className="text-[11px] text-[#94A3B8]">
                    Pops your connected wallet (MetaMask / Coinbase) to send ${selectedOrder.total_usd?.toFixed(2)} {selectedOrder.payment_token || 'USDC'} on {selectedOrder.chain?.toUpperCase()}. No hot wallet required.
                  </p>
                  <Button
                    onClick={async () => {
                      setSendingRefund(true);
                      try {
                        const { txHash } = await sendUsdcRefund({
                          to: selectedOrder.wallet_address,
                          amountUsd: selectedOrder.total_usd,
                          chain: selectedOrder.chain,
                        });
                        setRefundTxHash(txHash);
                        toast.success('Refund sent — now click "Record Refund" to flip status');
                      } catch (err) {
                        toast.error(err?.message || 'Wallet rejected or failed');
                      } finally { setSendingRefund(false); }
                    }}
                    disabled={sendingRefund}
                    className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-black font-['JetBrains_Mono'] font-bold disabled:opacity-50"
                    data-testid="send-refund-from-wallet">
                    {sendingRefund ? 'Sending…' : `⚡ Send $${selectedOrder.total_usd?.toFixed(2)} ${selectedOrder.payment_token || 'USDC'} now`}
                  </Button>
                </div>

                {/* Step-by-step */}
                <ol className="text-xs text-[#94A3B8] space-y-1 list-decimal list-inside pl-1">
                  <li><span className="text-[#F59E0B]">Fastest:</span> tap "Send ${selectedOrder.total_usd?.toFixed(2)} {selectedOrder.payment_token || 'USDC'} now" above — auto-fills the hash</li>
                  <li><span className="text-[#94A3B8]">Manual:</span> send from any wallet, then paste tx hash OR full BaseScan/Etherscan URL below</li>
                  <li>Click Record Refund → status flips to Refunded</li>
                </ol>

                {/* Smart hash input */}
                <div className="space-y-1.5">
                  <Input placeholder="Paste tx hash OR BaseScan/Etherscan URL"
                         value={refundTxHash}
                         onChange={(e) => setRefundTxHash(e.target.value)}
                         className={`bg-[#11141D] font-mono text-xs ${hashValid ? 'border-[#10B981]' : refundTxHash ? 'border-[#EF4444]' : 'border-[#334155]'}`}
                         data-testid="refund-tx-input" />
                  {refundTxHash && hashValid && (
                    <p className="text-[11px] text-[#10B981] font-['JetBrains_Mono'] flex items-center gap-1">
                      <CheckCircle size={12} weight="fill" /> Hash extracted: {extractTxHash(refundTxHash).slice(0, 10)}…{extractTxHash(refundTxHash).slice(-6)}
                    </p>
                  )}
                  {refundTxHash && !hashValid && (
                    <p className="text-[11px] text-[#EF4444] font-['JetBrains_Mono']">No valid 0x + 64-hex hash found in this input</p>
                  )}
                </div>

                <Textarea placeholder="Notes (visible in buyer's order history)"
                          value={refundNotes}
                          onChange={(e) => setRefundNotes(e.target.value)}
                          className="bg-transparent border-[#334155]" rows={2}
                          data-testid="refund-notes-input" />
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundDialogOpen(false)} disabled={resolving} className="border-[#334155]">Cancel</Button>
            <Button onClick={issueRefund} disabled={resolving || !isValidTxHash(extractTxHash(refundTxHash))}
                    className="bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-40" data-testid="confirm-refund">
              {resolving ? 'Recording…' : 'Record Refund → flip to Refunded'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatusTab = ({ active, onClick, label, count, color, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2 rounded-lg font-['JetBrains_Mono'] text-sm transition ${active ? 'bg-white/10 border' : 'bg-[#11141D] border'}`}
    style={{ borderColor: active ? color : '#1E293B', color: active ? color : '#94A3B8' }}>
    {label} <span className="ml-1 text-xs opacity-70">({count})</span>
  </button>
);

const Section = ({ title, children }) => (
  <div className="bg-[#05050A] rounded p-3 space-y-1">
    <h4 className="font-['JetBrains_Mono'] text-[#007AFF] text-sm mb-2">{title}</h4>
    {children}
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between text-xs"><span className="text-[#94A3B8]">{label}</span><span className="text-white font-mono">{value}</span></div>
);

export default AdminWeb3Orders;
